import {
  EnvConfig,
  Media,
  MediaType,
  useFindMedia,
} from '@gripp/shared-logic';
import { ImageGalleryImage } from '@gripp/shared-ui';
import { ReactNode, useMemo } from 'react';
import {
  FilePreviewPanel,
} from '../../assets/components/filesFilePreviewPanel';
import { GalleryPanel } from '../../assets/components/filesGalleryPanel';
import { VideoPanel } from '../../assets/components/filesVideoPanel';

const TITLE_MAX_LENGTH = 30;
const TITLE_TRUNCATE_LENGTH = 27;

export const truncateTitle = (
  name: string | undefined,
  fallback: string
): string => {
  const value = name ?? fallback;
  return value.length > TITLE_MAX_LENGTH
    ? `${value.substring(0, TITLE_TRUNCATE_LENGTH)}...`
    : value;
};

export const buildPreviewMain = (
  selectedMedia: Media | null,
  galleryImages: ImageGalleryImage[]
): ReactNode => {
  if (!selectedMedia) return null;
  if (selectedMedia.type === MediaType.Image) {
    return (
      <GalleryPanel
        images={galleryImages}
        initialIndex={0}
        disableImageChange
      />
    );
  }
  if (selectedMedia.type === MediaType.Video) {
    return <VideoPanel media={selectedMedia} />;
  }
  return <FilePreviewPanel media={selectedMedia} />;
};

export const useFilePreviewGallery = (
  selectedMedia: Media | null
): ImageGalleryImage[] =>
  useMemo(() => {
    if (!selectedMedia || selectedMedia.type !== MediaType.Image) return [];
    return [
      {
        id: selectedMedia.id,
        url: `${EnvConfig.mediaUrl}/images/${selectedMedia.id}/original.${selectedMedia.extension}`,
        name: selectedMedia.name,
      },
    ];
  }, [selectedMedia]);

export const usePreviewMedia = (fileId: string | null) => {
  const fetchedMedia = useFindMedia({
    filter: fileId ? { id: { eq: fileId } } : {},
  });

  const selectedMedia: Media | null = useMemo(
    () => fetchedMedia?.[0] ?? null,
    [fetchedMedia]
  );

  const galleryImages = useFilePreviewGallery(selectedMedia);

  return { selectedMedia, galleryImages };
};

export type PreviewMediaResult = ReturnType<typeof usePreviewMedia>;
